import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "../client";
import type {
  Transcript,
  TranscriptDetail,
  TranscriptUploadRequest,
  TranscriptUploadResponse,
} from "../types";

const siteTranscriptsKey = (siteId: string) =>
  ["sites", siteId, "transcripts"] as const;
const transcriptKey = (id: string) => ["transcripts", id] as const;

export function useSiteTranscripts(siteId: string | undefined) {
  return useQuery<Transcript[]>({
    queryKey: siteId ? siteTranscriptsKey(siteId) : ["transcripts", "disabled"],
    queryFn: () => apiRequest(`/api/sites/${siteId}/transcripts`),
    enabled: Boolean(siteId),
  });
}

/**
 * Polls the transcript detail endpoint every 3s while status is still
 * pending or processing. Stops polling (and caches the final state)
 * once extraction completes or fails.
 */
export function useTranscript(id: string | undefined) {
  return useQuery<TranscriptDetail>({
    queryKey: id ? transcriptKey(id) : ["transcripts", "disabled"],
    queryFn: () => apiRequest(`/api/transcripts/${id}`),
    enabled: Boolean(id),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 3000;
      return data.status === "complete" || data.status === "failed"
        ? false
        : 3000;
    },
  });
}

export function useReprocessTranscript(transcriptId: string) {
  const qc = useQueryClient();
  return useMutation<Transcript, Error, void>({
    mutationFn: () =>
      apiRequest(`/api/transcripts/${transcriptId}/reprocess`, {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: transcriptKey(transcriptId) });
      qc.invalidateQueries({ queryKey: ["sites"] });
    },
  });
}

export function useRequestTranscriptUpload(siteId: string) {
  const qc = useQueryClient();
  return useMutation<
    TranscriptUploadResponse,
    Error,
    TranscriptUploadRequest
  >({
    mutationFn: (payload) =>
      apiRequest(`/api/sites/${siteId}/transcripts`, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: siteTranscriptsKey(siteId) }),
  });
}

/**
 * Upload raw bytes to a presigned S3 URL. Bypasses the apiRequest wrapper
 * because the URL is on AWS, not our API — and we need to use the exact
 * method + headers returned by the server.
 */
export async function uploadToPresignedUrl(params: {
  url: string;
  method: "PUT";
  headers: Record<string, string>;
  body: Blob | File | string;
}): Promise<void> {
  const res = await fetch(params.url, {
    method: params.method,
    headers: params.headers,
    body: params.body,
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`S3 upload failed (${res.status}): ${body}`);
  }
}
